import React from 'react';
import {
  Box, ButtonBase, Checkbox, TextField, useMediaQuery, useTheme,
} from '@mui/material';
import { prettifyRut } from 'react-rut-formatter';
import analytics from '../../../../../../utils/analytics';
import numeroDocumento from '../../../../../../assets/numero_documento.webp';
import AlternativeEvaluationLink, {
  AlternativeEvaluationOptions,
} from '../../../../../../components/CannotCompleteVerificationDialog/redesign';
import PlrModal from '../../../landing/PlrModal';
import PlrOptions from '../../../landing/PlrOptions';
import { IndeterminateBar } from '../../../landing/PlrWaiting';
import AgentLink from '../../../landing/AgentLink';
import {
  PlrButton, PlrFieldLabel, PlrLead, PlrNotice, PlrTextLink, plrInputSx,
} from '../../../landing/ui';
import { plr } from '../../../landing/tokens';
import { checkCircleIcon, shieldCheckIcon } from '../../../landing/icons';
import { experianErrorSwitch, handleSubmit, termsAndConditions } from './components/ExperianForm/helpers';
import type { ExperianFormProps } from './components/ExperianForm/types';

const CATEGORY = 'PLR - SITUACION LABORAL';
const EXTERNAL_INFO_ERROR = 'No pudimos procesar tu evaluación porque uno de nuestros proveedores de información está temporalmente fuera de servicio. Vuelve a intentarlo más tarde.';
// Errores de Experian tras los que se puede volver a pedir el desafío.
const RETRYABLE_CHALLENGE = ['10006', '10001'];
const RETRYABLE_RESULT = ['10002', '10007', '10307', '10012'];
// Reintentos permitidos (después, solo evaluación alternativa o un ejecutivo).
const MAX_RETRIES = 2;

const EMPTY_CHALLENGE = {
  data: {
    RESUMEN: { RUT: '', USUARIO: '', IDCHALLENGE: '' },
    PREGUNTAS: [],
    codigoRetorno: '',
  },
  success: false,
  errors: [],
};

type ExperianFlowProps = Omit<ExperianFormProps, 'handleChange' | 'handleUnemployedFallback' | 'patchManualVerificationFallback'> & {
  handleChange: (event: React.ChangeEvent<HTMLInputElement>) => void,
  onUnemployed: () => Promise<void>,
  onManualVerification: () => Promise<void>,
};

function StageLead({ children }: { children: React.ReactNode }) {
  return <PlrLead sx={{ mt: '-6px' }}>{children}</PlrLead>;
}

// NEEDS_ICP (rediseño): verificación de Experian en tres etapas (número de documento →
// preguntas → resultado). En desktop van en la página; en mobile, en una hoja que se abre con
// "Verificar mi identidad" (como el diálogo del flujo original). Usa la misma lógica de
// consultas que ExperianForm (handleSubmit en helpers) y los mismos eventos de analytics.
export default function ExperianFlow(props: ExperianFlowProps) {
  const {
    values, handleChange, setFieldValue, queriesArray, handleCurrentData, handleStep,
    handleEnableLoading, onUnemployed, onManualVerification,
  } = props;
  const [stage, setStage] = React.useState(0);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(false);
  const [externalError, setExternalError] = React.useState(false);
  const [accepted, setAccepted] = React.useState(false);
  const [answers, setAnswers] = React.useState<Record<string, string>>({});
  const [serialHelpOpen, setSerialHelpOpen] = React.useState(false);
  const [termsOpen, setTermsOpen] = React.useState(false);
  const errorPageSent = React.useRef(false);
  const theme = useTheme();
  const desktop = useMediaQuery(theme.breakpoints.up('sm'), { noSsr: true });
  const [sheetOpen, setSheetOpen] = React.useState(false);
  // Mobile: INGRESO DATOS EXPERIAN sale al montar el paso, antes de abrir la hoja; este clic
  // muestra quién la abre de verdad.
  const openLabel = stage === 0 && !error ? 'Verificar mi identidad' : 'Continuar verificación';
  const openSheet = (text: string) => {
    analytics.track('Click Boton', { text, initiator: 'usuario' });
    setSheetOpen(true);
  };

  React.useEffect(() => {
    analytics.page(values.isRedirect ? `PLR - ${values.isRedirect}` : CATEGORY, 'CONSULTA PREVIRED');
    handleCurrentData({ ...values, isRedirect: false });
  }, []);

  React.useEffect(() => {
    if (stage === 0) analytics.page(CATEGORY, 'INGRESO DATOS EXPERIAN');
    if (stage === 1) analytics.page(CATEGORY, 'CHALLENGE EXPERIAN');
    errorPageSent.current = false;
    handleSubmit({
      values,
      step: stage,
      handleSetError: setError,
      handleSetLoading: setLoading,
      queriesArray,
      setFieldValue,
      handleCurrentData,
      handleStep,
      handleEnableLoading,
      handleSetErrorExternalInformation: setExternalError,
    });
  }, [stage]);

  const questions = values.experianChallenge?.data?.PREGUNTAS || [];
  React.useEffect(() => {
    const initial: Record<string, string> = {};
    questions.forEach((q) => { initial[q.CODIGO_PREGUNTA] = ''; });
    setAnswers(initial);
  }, [values.experianChallenge]);

  React.useEffect(() => {
    setFieldValue('experianChallengeResponses', answers);
  }, [answers]);

  const challengeCode = values.experianChallenge?.errors?.[0]?.errorCode;
  const resultErrors = values.experianChallengeResult?.errors || [];
  const resultCode = resultErrors[0]?.errorCode;

  React.useEffect(() => {
    if (!error || errorPageSent.current) return;
    errorPageSent.current = true;
    if (stage === 1) {
      analytics.page(CATEGORY, 'ERROR INGRESO DATOS EXPERIAN', {
        errorType: challengeCode, attempt: values.experianRetries,
      });
      analytics.page(CATEGORY, 'DIALOGO NO PUEDE COMPLETAR VERIFICACION');
    }
    if (stage === 2) {
      analytics.page(CATEGORY, 'ERROR CHALLENGE EXPERIAN', {
        errorType: resultCode, attempt: values.experianRetries,
      });
      analytics.page(CATEGORY, 'DIALOGO ERROR EXPERIAN');
    }
  }, [error, stage]);

  const retriesLeft = values.experianRetries < MAX_RETRIES;
  const maxRetriesReached = values.experianRetries > MAX_RETRIES;

  const handleRetry = () => {
    analytics.track('Click Boton', { initiator: 'usuario', text: 'Reintentar' });
    setFieldValue('experianRetries', values.experianRetries + 1);
    setFieldValue('experianChallenge', EMPTY_CHALLENGE);
    setFieldValue('experianChallengeResponses', {});
    setError(false);
    setExternalError(false);
    setStage(0);
  };

  const handleOpenSerialHelp = () => {
    analytics.track('Click Link', { text: '¿Qué es el número de documento/serie?', initiator: 'usuario' });
    setSerialHelpOpen(true);
  };
  const handleOpenTerms = () => {
    analytics.track('Click Link', { text: 'condiciones de uso del servicio', initiator: 'usuario' });
    setTermsOpen(true);
  };
  const handleAccept = () => {
    analytics.track('Click Checkbox', { text: 'Acepto las condiciones de uso del servicio', initiator: 'usuario' });
    setAccepted((value) => !value);
  };

  const idLine = values.identificationId ? ` ID de verificación: ${values.identificationId}.` : '';
  let errorMessage = '';
  let canRetry = false;
  if (error && stage === 1) {
    errorMessage = externalError ? EXTERNAL_INFO_ERROR
      : (challengeCode && experianErrorSwitch(challengeCode))
        || 'Ocurrió un error al preparar tus preguntas. Vuelve a intentarlo más tarde.';
    canRetry = !externalError && RETRYABLE_CHALLENGE.includes(challengeCode) && retriesLeft;
  }
  if (error && stage === 2) {
    const retryable = resultErrors.some((err) => RETRYABLE_RESULT.includes(err.errorCode));
    canRetry = !externalError && retryable && retriesLeft;
    if (externalError) errorMessage = EXTERNAL_INFO_ERROR;
    else if (retryable) {
      // Sin texto propio en experianErrorSwitch (salía "Ha ocurrido un error (10002)").
      errorMessage = canRetry
        ? 'No pudimos verificar tu identidad con esas respuestas. Puedes intentarlo de nuevo con preguntas nuevas.'
        : 'No pudimos verificar tu identidad con esas respuestas y ya usaste los reintentos.';
    } else {
      errorMessage = resultErrors.map((err) => experianErrorSwitch(err.errorCode)).join(' ')
        || 'No pudimos verificar tus respuestas. Vuelve a intentarlo más tarde.';
    }
  }

  const errorBlock = error && stage > 0 && (
    <>
      <PlrNotice
        tone="error"
        testId="experian-error"
        action={canRetry ? (
          <PlrTextLink onClick={handleRetry} testId="experian-retry">
            {`Reintentar (${MAX_RETRIES - values.experianRetries} ${MAX_RETRIES - values.experianRetries === 1 ? 'intento' : 'intentos'})`}
          </PlrTextLink>
        ) : undefined}
      >
        {`${errorMessage}${idLine}`}
      </PlrNotice>
      <Box sx={{
        mt: '6px', display: 'flex', flexDirection: 'column', gap: '10px',
      }}
      >
        <Box
          component="h2"
          sx={{
            m: 0, fontSize: { xs: 16, sm: 17 }, fontWeight: 700, color: plr.ink,
          }}
        >
          ¿No puedes completar la verificación?
        </Box>
        <Box component="p" sx={{ m: '-4px 0 0', fontSize: { xs: 13.5, sm: 14 }, color: plr.body }}>
          Puedes seguir con una de estas opciones:
        </Box>
        <AlternativeEvaluationOptions
          onUnemployedClick={onUnemployed}
          onManualEvaluationClick={onManualVerification}
        />
      </Box>
    </>
  );

  let body: React.ReactElement;
  if (stage === 0) {
    body = (
      <>
        <StageLead>
          {desktop
            ? 'Para leer tus cotizaciones en Previred, Experian confirma que eres tú con el número de documento de tu cédula.'
            : 'Ingresa el número de documento de tu cédula y acepta la consulta a Previred.'}
        </StageLead>
        <Box sx={{
          display: 'flex', flexDirection: 'column', flex: 1, gap: '18px',
        }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <PlrFieldLabel
              htmlFor="serialNumber"
              aside={(
                <PlrTextLink onClick={handleOpenSerialHelp} testId="experian-serial-help" sx={{ fontSize: 13.5 }}>
                  ¿Dónde lo encuentro?
                </PlrTextLink>
              )}
            >
              N° de documento
            </PlrFieldLabel>
            <TextField
              id="serialNumber"
              name="serialNumber"
              data-pw="experian-serial-input"
              value={values.serialNumber}
              onChange={handleChange}
              disabled={loading || maxRetriesReached}
              placeholder="Ej: 100100001"
              autoComplete="off"
              inputProps={{ 'aria-describedby': 'experian-serial-helper' }}
              sx={plrInputSx}
            />
            <Box id="experian-serial-helper" sx={{ mx: '4px', fontSize: 13, color: plr.muted }}>
              {`Cédula del RUT ${prettifyRut(values.rut)}`}
            </Box>
          </Box>
          {maxRetriesReached && (
            <PlrNotice tone="error" action={<AgentLink cta="experian max retries" />}>
              Llegaste al máximo de reintentos. Si tienes problemas para verificarte, contáctanos.
            </PlrNotice>
          )}
          {error && (
            <PlrNotice tone="error">
              {externalError ? EXTERNAL_INFO_ERROR : 'Ocurrió un error. Vuelve a intentarlo o contáctanos.'}
            </PlrNotice>
          )}
          <Box sx={{
            mt: 'auto', display: 'flex', flexDirection: 'column', gap: '14px',
          }}
          >
            <Box
              component="label"
              sx={{
                display: 'flex',
                gap: '10px',
                alignItems: 'flex-start',
                fontSize: { xs: 13.5, sm: 14 },
                lineHeight: 1.45,
                color: plr.ink,
                cursor: 'pointer',
              }}
            >
              <Checkbox
                data-pw="experian-terms"
                checked={accepted}
                onClick={handleAccept}
                disabled={loading}
                disableRipple
                sx={{
                  p: 0,
                  mt: '-1px',
                  color: plr.faint,
                  '&.Mui-checked': { color: plr.ink },
                  '& .MuiSvgIcon-root': { fontSize: 24 },
                }}
              />
              <span>
                Acepto las
                {' '}
                <PlrTextLink onClick={handleOpenTerms} testId="experian-terms-link" sx={{ display: 'inline', verticalAlign: 'baseline' }}>
                  condiciones de uso del servicio
                </PlrTextLink>
                : autorizo a mi AFP a entregar mis últimas 12 cotizaciones vía Previred para
                esta solicitud.
              </span>
            </Box>
            <PlrButton
              testId="experian-serial-submit"
              onClick={() => setStage(1)}
              disabled={!accepted || !values.serialNumber || maxRetriesReached}
              loading={loading}
            >
              Continuar
            </PlrButton>
            <Box sx={{ fontSize: 13.5 }}>
              <AlternativeEvaluationLink
                onUnemployedClick={onUnemployed}
                onManualEvaluationClick={onManualVerification}
                label="No puedo verificarme"
                title="¿No puedes completar la verificación?"
                pageCall={{ category: CATEGORY, name: 'DIALOGO QUERO EVALUACION ALTERNATIVA' }}
              />
            </Box>
          </Box>
        </Box>
        <PlrModal
          open={serialHelpOpen}
          onClose={() => setSerialHelpOpen(false)}
          title="¿Dónde está el número de documento?"
          titleId="experian-serial-help-title"
          testId="experian-serial-help-dialog"
          width={560}
        >
          <Box
            component="p"
            sx={{
              m: '-6px 0 0', fontSize: { xs: 14, sm: 15 }, lineHeight: 1.45, color: plr.body,
            }}
          >
            Búscalo en tu cédula como «Número documento» o, en cédulas antiguas, «número de
            serie».
          </Box>
          <Box
            component="img"
            src={numeroDocumento}
            alt="Ubicación del número de documento en la cédula de identidad"
            sx={{ width: '100%', borderRadius: '16px', display: 'block' }}
          />
          <Box component="span" sx={{ fontSize: 12.5, color: plr.muted }}>
            Fuente: Registro Civil de Chile
          </Box>
        </PlrModal>
        <PlrModal
          open={termsOpen}
          onClose={() => setTermsOpen(false)}
          title="Condiciones de uso del servicio"
          titleId="experian-terms-title"
          testId="experian-terms-dialog"
          width={520}
        >
          <Box
            component="p"
            sx={{
              m: '-6px 0 0', fontSize: { xs: 14, sm: 15 }, lineHeight: 1.55, color: plr.body,
            }}
          >
            {termsAndConditions.replace(/\s+/g, ' ')}
          </Box>
        </PlrModal>
      </>
    );
  } else if (stage === 1) {
    const allAnswered = questions.length > 0
      && questions.every((q) => answers[q.CODIGO_PREGUNTA]);
    body = (
      <>
        <StageLead>Experian te hace unas preguntas para confirmar que eres tú.</StageLead>
        <Box sx={{
          display: 'flex', flexDirection: 'column', flex: 1, gap: '20px',
        }}
        >
          {!error && questions.length === 0 && (
            <Box sx={{
              mt: '8px', display: 'flex', flexDirection: 'column', gap: '12px',
            }}
            >
              <IndeterminateBar label="Preparando tus preguntas" />
              <Box component="p" aria-live="polite" sx={{ m: 0, fontWeight: 600, color: plr.ink }}>
                Preparando tus preguntas…
              </Box>
            </Box>
          )}
          {errorBlock}
          {!error && questions.map((question, index) => (
            <Box
              key={question.CODIGO_PREGUNTA}
              sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}
            >
              <Box
                id={`experian-q-${question.CODIGO_PREGUNTA}`}
                sx={{
                  fontSize: { xs: 15, sm: 16 }, fontWeight: 700, color: plr.ink, lineHeight: 1.35,
                }}
              >
                {`${index + 1}. ${question.PREGUNTA}`}
              </Box>
              <PlrOptions
                dense
                options={question.ALTERNATIVAS.map((alt) => ({
                  value: alt.CODIGO_RESPUESTA, title: alt.RESPUESTA,
                }))}
                value={answers[question.CODIGO_PREGUNTA] || ''}
                onChange={(value) => setAnswers((prev) => ({
                  ...prev, [question.CODIGO_PREGUNTA]: value,
                }))}
                labelledBy={`experian-q-${question.CODIGO_PREGUNTA}`}
                testIdPrefix={`experian-q${index}`}
                disabled={loading}
              />
            </Box>
          ))}
          {!error && questions.length > 0 && (
            <Box sx={{
              mt: 'auto', pt: '4px', display: 'flex', flexDirection: 'column', gap: '14px',
            }}
            >
              <PlrButton
                testId="experian-questions-submit"
                onClick={() => setStage(2)}
                disabled={!allAnswered}
                loading={loading}
              >
                Verificar respuestas
              </PlrButton>
              <AlternativeEvaluationLink
                onUnemployedClick={onUnemployed}
                onManualEvaluationClick={onManualVerification}
                label="No puedo responder las preguntas"
                title="¿No puedes completar la verificación?"
                pageCall={{ category: CATEGORY, name: 'DIALOGO NO PUEDE COMPLETAR VERIFICACION' }}
              />
            </Box>
          )}
        </Box>
      </>
    );
  } else {
    body = (
      <>
        <StageLead>Revisamos tus respuestas con Experian.</StageLead>
        <Box sx={{
          display: 'flex', flexDirection: 'column', flex: 1, gap: '16px',
        }}
        >
          {(loading || values.experianChallengeResult?.success) && !error && (
          <Box
            data-pw="experian-verifying"
            sx={{
              mt: '8px', display: 'flex', flexDirection: 'column', gap: '12px',
            }}
          >
            <IndeterminateBar label="Verificando" />
            <Box component="p" aria-live="polite" sx={{ m: 0, fontWeight: 600, color: plr.ink }}>
              {values.experianChallengeResult?.success
                ? 'Verificación completa. Te llevamos al siguiente paso…'
                : 'Verificando tus respuestas…'}
            </Box>
          </Box>
          )}
          {errorBlock}
        </Box>
      </>
    );
  }

  if (desktop) return body;

  // Mobile: la página resume el paso y la verificación se hace en una hoja.
  let status = 'Pendiente';
  let sheetTitle = 'Confirma tu cédula';
  if (stage === 1) {
    status = 'Falta responder las preguntas';
    sheetTitle = 'Responde las preguntas';
  }
  if (stage === 2) {
    status = values.experianChallengeResult?.success ? 'Verificación completa' : 'Verificando…';
    sheetTitle = 'Verificando tu identidad';
  }
  if (error && stage > 0) {
    status = 'No pudimos verificarte';
    sheetTitle = 'No pudimos verificarte';
  }
  const done = stage === 2 && !!values.experianChallengeResult?.success;

  return (
    <>
      <StageLead>
        Para leer tus cotizaciones en Previred, Experian confirma que eres tú con tu cédula y
        unas preguntas.
      </StageLead>
      <Box sx={{
        display: 'flex', flexDirection: 'column', flex: 1, gap: '16px',
      }}
      >
        <ButtonBase
          type="button"
          data-pw="experian-row"
          onClick={() => openSheet('Verificación con Experian')}
          sx={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            p: '12px',
            borderRadius: '16px',
            background: '#FFFFFF',
            boxShadow: `inset 0 0 0 1px ${plr.border}`,
            textAlign: 'left',
            fontFamily: plr.font,
          }}
        >
          <Box
            component="span"
            aria-hidden="true"
            sx={{
              flexShrink: 0,
              width: 36,
              height: 36,
              borderRadius: '12px',
              background: plr.surface,
              color: done ? '#1E7B4F' : plr.accent,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {done ? checkCircleIcon({ size: 20, strokeWidth: 2.2 }) : shieldCheckIcon({ size: 19 })}
          </Box>
          <Box
            component="span"
            sx={{
              flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '2px',
            }}
          >
            <Box component="span" sx={{ fontSize: 15, fontWeight: 700, color: plr.ink }}>
              Verificación con Experian
            </Box>
            <Box
              component="span"
              sx={{
                fontSize: 13,
                fontWeight: 600,
                color: error && stage > 0 ? plr.danger : plr.accent,
              }}
            >
              {status}
            </Box>
          </Box>
        </ButtonBase>
        <Box sx={{ mt: 'auto', display: 'flex', flexDirection: 'column' }}>
          <PlrButton testId="experian-open" onClick={() => openSheet(openLabel)}>
            {openLabel}
          </PlrButton>
        </Box>
      </Box>
      <PlrModal
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        title={sheetTitle}
        titleId="experian-sheet-title"
        testId="experian-sheet"
      >
        {body}
      </PlrModal>
    </>
  );
}
